import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ClipboardList, Filter, RefreshCw, BarChart3, Loader2, Eye, Edit, User, FileSpreadsheet } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/lib/AuthContext';
import * as XLSX from 'xlsx';
import { SubstituteFilledBadge } from '@/components/guru-pengganti/FilledByCell';
import { formatWIB } from '@/lib/datetime';
import KehadiranPerkiraanNotice from '@/components/journal/KehadiranPerkiraanNotice';
import { ASAL_INPUT, asalInput } from '@/lib/jurnalAsal';

// Badge asal input jurnal: Jurnal Presisi (tepat waktu) vs Catat Jurnal (diisi di luar jam).
function BadgeAsalInput({ journal }) {
  const a = ASAL_INPUT[asalInput(journal)];
  return <Badge variant="outline" className={`whitespace-nowrap text-xs ${a.badge}`} data-testid={`asal-input-${journal.id}`}>{a.label}</Badge>;
}

export default function AdminJurnalRekapPage() {
  const navigate = useNavigate();
  const { activeRole } = useAuth();
  const canEdit = activeRole !== 'kepala_sekolah';
  const [data, setData] = useState({ items: [], total: 0, summary: {} });
  const [statsByTeacher, setStatsByTeacher] = useState([]);
  const [loading, setLoading] = useState(true);
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [filters, setFilters] = useState({
    class_id: 'all', teacher_id: 'all', subject_id: 'all', asal_input: 'all',
    start_date: '', end_date: '',
  });
  const [detailDialog, setDetailDialog] = useState({ open: false, journal: null });
  const [attendanceDetail, setAttendanceDetail] = useState(null);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, journal: null });
  const [editForm, setEditForm] = useState({ materi: '', catatan: '', jenis_izin: '', attendances: [] });
  const [editSaving, setEditSaving] = useState(false);

  const buildParams = () => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v && v !== 'all') p[k] = v;
    });
    return p;
  };

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get('/admin/jurnal', { params: buildParams() }),
        api.get('/admin/jurnal/stats-by-teacher'),
      ]);
      setData(r.data); setStatsByTeacher(s.data);
    } catch (e) { /* */ } finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      const [c, sub, u] = await Promise.all([
        api.get('/classes'), api.get('/subjects'), api.get('/users'),
      ]);
      setClasses(c.data); setSubjects(sub.data);
      setTeachers(u.data.filter((x) => x.roles?.some((rr) => ['guru', 'wali_kelas', 'guru_piket', 'guru_bk', 'guru_tata_tertib', 'guru_ekstrakurikuler'].includes(rr))));
      await load();
    })();
    // eslint-disable-next-line
  }, []);

  const reset = () => {
    setFilters({ class_id: 'all', teacher_id: 'all', subject_id: 'all', asal_input: 'all', start_date: '', end_date: '' });
    setTimeout(() => load(), 50);
  };

  const loadAttendanceDetail = async (journalId) => {
    try {
      setLoadingAttendance(true);
      const res = await api.get(`/journals/${journalId}/attendance`);
      setAttendanceDetail(res.data);
      setLoadingAttendance(false);
    } catch (err) {
      console.error('Error loading attendance detail:', err);
      setLoadingAttendance(false);
      toast.error('Gagal memuat detail kehadiran');
    }
  };

  const handleShowDetail = (journal) => {
    setDetailDialog({ open: true, journal });
    setAttendanceDetail(null);
    loadAttendanceDetail(journal.id);
  };

  // Detail kehadiran per status, dikelompokkan dari attendance_details -> teks
  // "Hadir: A, B | Sakit: C | Izin: - | Alpa: D" untuk satu sel kolom export.
  const formatAttendanceDetail = (j) => {
    const details = j.attendance_details || [];
    if (details.length === 0) return '-';
    const byStatus = { hadir: [], sakit: [], izin: [], alpa: [] };
    details.forEach((d) => {
      const status = d.status === 'alpha' ? 'alpa' : d.status;
      if (byStatus[status]) byStatus[status].push(d.student_name);
    });
    const labels = { hadir: 'Hadir', sakit: 'Sakit', izin: 'Izin', alpa: 'Alpa' };
    return Object.entries(byStatus)
      .map(([status, names]) => `${labels[status]}: ${names.length > 0 ? names.join(', ') : '-'}`)
      .join(' | ');
  };

  const exportCSV = () => {
    if (!data.items.length) return;
    const headers = ['Tanggal', 'JTM', 'Kelas', 'Mapel', 'Guru', 'Ruang', 'Asal Input', 'KD/Indikator', 'Materi/Pokok Bahasan (Terdaftar)', 'Materi', 'Catatan', 'Hadir', 'Sakit', 'Izin', 'Alpa', 'Detail Kehadiran Siswa'];
    const rows = data.items.map((j) => [
      formatWIB(j.started_at, ''), j.jtm_count || 1, j.class_name, j.subject_name, j.teacher_name, j.room_name,
      ASAL_INPUT[asalInput(j)].label, JSON.stringify(j.kd_indikator || ''), JSON.stringify(j.materi_nama || ''), JSON.stringify(j.materi), JSON.stringify(j.catatan || ''), j.siswa_hadir, j.siswa_sakit, j.siswa_izin, j.siswa_tidak_hadir,
      JSON.stringify(formatAttendanceDetail(j)),
    ]);
    const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `rekap-jurnal-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportExcel = () => {
    if (!data.items.length) return;

    // Prepare data for Excel
    const excelData = data.items.map((j) => ({
      'Tanggal': formatWIB(j.started_at, ''),
      'JTM': j.jtm_count || 1,
      'Kelas': j.class_name || '-',
      'Mapel': j.subject_name || '-',
      'Guru': j.teacher_name || '-',
      'Ruang': j.room_name || '-',
      'Asal Input': ASAL_INPUT[asalInput(j)].label,
      'KD/Indikator': j.kd_indikator || '-',
      'Materi/Pokok Bahasan (Terdaftar)': j.materi_nama || '-',
      'Materi': j.materi || '-',
      'Catatan': j.catatan || '-',
      'Hadir': j.siswa_hadir || 0,
      'Sakit': j.siswa_sakit || 0,
      'Izin': j.siswa_izin || 0,
      'Alpa': j.siswa_tidak_hadir || 0,
      'Detail Kehadiran Siswa': formatAttendanceDetail(j),
    }));

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Rekap Jurnal');

    // Set column widths
    const colWidths = [
      { wch: 20 }, // Tanggal
      { wch: 5 },  // JTM
      { wch: 15 }, // Kelas
      { wch: 25 }, // Mapel
      { wch: 25 }, // Guru
      { wch: 15 }, // Ruang
      { wch: 15 }, // Asal Input
      { wch: 25 }, // KD/Indikator
      { wch: 30 }, // Materi/Pokok Bahasan (Terdaftar)
      { wch: 40 }, // Materi
      { wch: 40 }, // Catatan
      { wch: 8 },  // Hadir
      { wch: 8 },  // Sakit
      { wch: 8 },  // Izin
      { wch: 8 },  // Alpa
      { wch: 60 }, // Detail Kehadiran Siswa
    ];
    ws['!cols'] = colWidths;

    // Generate and download
    XLSX.writeFile(wb, `rekap-jurnal-${Date.now()}.xlsx`);
    toast.success('File Excel berhasil diunduh');
  };

  const openEditDialog = async (journal) => {
    try {
      // Fetch full journal details with attendance
      const { data: fullJournal } = await api.get(`/jurnal/${journal.id}`);

      setEditDialog({ open: true, journal: fullJournal });
      setEditForm({
        materi: fullJournal.materi || '',
        catatan: fullJournal.catatan || '',
        jenis_izin: fullJournal.jenis_izin || '',
        attendances: fullJournal.attendance_details || [],
      });
    } catch (error) {
      console.error('Error loading journal:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Gagal memuat detail jurnal';
      toast.error(errorMsg);
    }
  };

  const handleEditSave = async () => {
    if (!editDialog.journal || !editForm.materi.trim()) {
      toast.error('Materi pembelajaran harus diisi');
      return;
    }

    setEditSaving(true);
    try {
      // Update journal materi, catatan, and jenis_izin
      await api.put(`/admin/jurnal/${editDialog.journal.id}`, {
        materi: editForm.materi,
        catatan: editForm.catatan,
        jenis_izin: editForm.jenis_izin,
      });

      // Update attendances
      await api.put(`/admin/jurnal/${editDialog.journal.id}/attendances`, {
        attendances: editForm.attendances,
      });

      toast.success('Jurnal berhasil diperbarui');
      setEditDialog({ open: false, journal: null });
      load(); // Reload data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memperbarui jurnal');
    } finally {
      setEditSaving(false);
    }
  };

  const updateAttendanceStatus = (studentId, newStatus) => {
    setEditForm(prev => ({
      ...prev,
      attendances: prev.attendances.map(att =>
        att.student_id === studentId ? { ...att, status: newStatus } : att
      ),
    }));
  };

  return (
    <div className="space-y-6">
      <div>
        <Badge className="bg-[#006837]/10 text-[#006837] border-[#006837]/20 mb-2"><ClipboardList className="h-3 w-3 mr-1" /> Data Jurnal</Badge>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Rekap Jurnal Mengajar</h1>
        <p className="text-sm text-slate-600 mt-1">{data.total} entri jurnal {filters.class_id !== 'all' || filters.teacher_id !== 'all' || filters.subject_id !== 'all' || filters.asal_input !== 'all' || filters.start_date || filters.end_date ? '(terfilter)' : ''}</p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="h-4 w-4 text-[#006837]" />
            <h2 className="text-sm font-semibold">Filter Data</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
            <div>
              <Label className="text-xs">Kelas</Label>
              <Select value={filters.class_id} onValueChange={(v) => setFilters({...filters, class_id: v})}>
                <SelectTrigger data-testid="rekap-filter-class"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Kelas</SelectItem>
                  {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Guru</Label>
              <Select value={filters.teacher_id} onValueChange={(v) => setFilters({...filters, teacher_id: v})}>
                <SelectTrigger data-testid="rekap-filter-teacher"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Guru</SelectItem>
                  {teachers.map((t) => <SelectItem key={t.id} value={t.id}>{t.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Mata Pelajaran</Label>
              <Select value={filters.subject_id} onValueChange={(v) => setFilters({...filters, subject_id: v})}>
                <SelectTrigger data-testid="rekap-filter-subject"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Mapel</SelectItem>
                  {subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Asal Input</Label>
              <Select value={filters.asal_input} onValueChange={(v) => setFilters({...filters, asal_input: v})}>
                <SelectTrigger data-testid="rekap-filter-asal-input"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Asal Input</SelectItem>
                  {Object.entries(ASAL_INPUT).map(([kode, a]) => <SelectItem key={kode} value={kode}>{a.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Dari Tanggal</Label><Input type="date" value={filters.start_date} onChange={(e) => setFilters({...filters, start_date: e.target.value})} data-testid="rekap-filter-start" /></div>
            <div><Label className="text-xs">Sampai Tanggal</Label><Input type="date" value={filters.end_date} onChange={(e) => setFilters({...filters, end_date: e.target.value})} data-testid="rekap-filter-end" /></div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" size="sm" onClick={reset} data-testid="rekap-reset">Reset</Button>
            <Button onClick={load} className="bg-[#006837] hover:bg-[#0B7A3B] gap-1" size="sm" data-testid="rekap-apply"><RefreshCw className="h-3.5 w-3.5" /> Terapkan Filter</Button>
            <Button onClick={exportExcel} variant="outline" size="sm" disabled={!data.items.length} className="gap-1" data-testid="rekap-export-excel"><FileSpreadsheet className="h-3.5 w-3.5" /> Excel</Button>
            <Button onClick={exportCSV} variant="outline" size="sm" disabled={!data.items.length} data-testid="rekap-export">CSV</Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <SummaryCard label="Total Jurnal" value={data.total} color="slate" testid="rekap-stat-total" />
        <SummaryCard label="Total Hadir" value={data.summary?.total_hadir || 0} color="emerald" testid="rekap-stat-hadir" />
        <SummaryCard label="Total Sakit" value={data.summary?.total_sakit || 0} color="amber" testid="rekap-stat-sakit" />
        <SummaryCard label="Total Izin" value={data.summary?.total_izin || 0} color="blue" testid="rekap-stat-izin" />
        <SummaryCard label="Total Alpa" value={data.summary?.total_alpa || 0} color="rose" testid="rekap-stat-alpa" />
      </div>

      <Tabs defaultValue="items">
        <TabsList>
          <TabsTrigger value="items" data-testid="rekap-tab-items">Daftar Jurnal ({data.total})</TabsTrigger>
          <TabsTrigger value="by-teacher" data-testid="rekap-tab-teacher"><BarChart3 className="h-4 w-4 mr-1" /> Per Guru</TabsTrigger>
        </TabsList>

        <TabsContent value="items" className="mt-4">
          {loading ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : (
            <Card><CardContent className="p-0"><div className="overflow-x-auto"><Table data-testid="admin-jurnal-rekap-table">
              <TableHeader><TableRow>
                <TableHead>Tanggal</TableHead><TableHead>Jam Ke</TableHead><TableHead className="text-center">JTM</TableHead><TableHead>Kelas</TableHead><TableHead>Mapel</TableHead><TableHead>Guru</TableHead><TableHead>Ruang</TableHead><TableHead>Materi</TableHead><TableHead>Jenis Izin</TableHead><TableHead>Catatan</TableHead><TableHead className="text-center">Kehadiran</TableHead><TableHead>Diisi oleh</TableHead><TableHead>Asal Input</TableHead><TableHead className="text-center">Aksi</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.items.length === 0 ? <TableRow><TableCell colSpan={14} className="text-center py-8 text-slate-500">Tidak ada data</TableCell></TableRow> :
                  data.items.map((j) => (
                    <TableRow key={j.id}>
                      <TableCell className="text-xs whitespace-nowrap">{formatWIB(j.started_at, '', { dateStyle: 'short', timeStyle: 'short' })}</TableCell>
                      <TableCell>
                        <div className="text-xs font-medium text-slate-700">
                          {j.jam_ke || '-'}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300 font-semibold">
                          {j.jtm_count || 1} JTM
                        </Badge>
                      </TableCell>
                      <TableCell className="font-semibold">{j.class_name}</TableCell>
                      <TableCell><span className="text-xs font-mono bg-slate-100 rounded px-1">{j.subject_code}</span> {j.subject_name}</TableCell>
                      <TableCell className="text-sm">{j.teacher_name}</TableCell>
                      <TableCell className="font-mono text-xs">{j.room_name}</TableCell>
                      <TableCell className="text-sm max-w-xs" title={j.materi}>
                        {j.kd_indikator && (
                          <div className="text-[11px] font-semibold text-[#006837] truncate">{j.kd_indikator}</div>
                        )}
                        {j.materi_nama && (
                          <div className="text-[11px] font-medium text-blue-700 truncate">{j.materi_nama}</div>
                        )}
                        <div className="truncate">{j.materi}</div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {j.jenis_izin ? (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300 text-xs">
                            {j.jenis_izin}
                          </Badge>
                        ) : (
                          <span className="text-slate-400 text-xs">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm max-w-xs truncate" title={j.catatan || '-'}>
                        {j.catatan || <span className="text-slate-400">-</span>}
                      </TableCell>
                      <TableCell className="text-center text-xs font-semibold">
                        <span className="text-emerald-700">{j.siswa_hadir || 0}</span>
                        <span className="text-slate-400 mx-0.5">/</span>
                        <span className="text-amber-600">{j.siswa_sakit || 0}</span>
                        <span className="text-slate-400 mx-0.5">/</span>
                        <span className="text-blue-600">{j.siswa_izin || 0}</span>
                        <span className="text-slate-400 mx-0.5">/</span>
                        <span className="text-rose-600">{j.siswa_tidak_hadir || 0}</span>
                      </TableCell>
                      <TableCell>
                        {j.fill_mode === 'substitute' ? (
                          <SubstituteFilledBadge journal={j} />
                        ) : j.fill_mode === 'piket' ? (
                          <Badge className="bg-amber-100 text-amber-700 border-amber-200 gap-1 text-xs" data-testid={`fill-badge-piket-${j.id}`}>
                            ✋ Piket{j.filled_by_name ? `: ${j.filled_by_name}` : ''}
                          </Badge>
                        ) : j.fill_mode === 'admin' ? (
                          <Badge className="bg-purple-100 text-purple-700 border-purple-200 text-xs">Admin</Badge>
                        ) : (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">Pengajar</Badge>
                        )}
                      </TableCell>
                      <TableCell><BadgeAsalInput journal={j} /></TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleShowDetail(j)}
                            className="h-7 px-2"
                          >
                            <Eye className="h-3 w-3" />
                          </Button>
                          {canEdit && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditDialog(j)}
                              className="h-7 px-2"
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table></div></CardContent></Card>
          )}
        </TabsContent>

        <TabsContent value="by-teacher" className="mt-4">
          <Card><CardContent className="p-5">
            <h2 className="text-base font-semibold mb-3">Statistik Pengisian Jurnal per Guru (TP Aktif)</h2>
            {statsByTeacher.length === 0 ? <div className="text-sm text-slate-500">Belum ada data jurnal</div> : (
              <div className="space-y-2" data-testid="rekap-by-teacher">
                {(() => {
                  const max = Math.max(...statsByTeacher.map((s) => s.count || 0), 1);
                  return statsByTeacher.map((s, idx) => (
                    <div key={s.teacher_id || idx} className="flex items-center gap-3">
                      <div className="w-48 truncate text-sm font-semibold">{s.teacher_name}</div>
                      <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
                        <div className="bg-gradient-to-r from-[#006837] to-[#0B7A3B] h-full flex items-center justify-end pr-2 text-xs font-bold text-white"
                          style={{ width: `${(s.count / max) * 100}%`, minWidth: '36px' }}>
                          {s.count}
                        </div>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      {/* Detail Dialog */}
      <Dialog open={detailDialog.open} onOpenChange={(open) => setDetailDialog({ open, journal: null })}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detail Jurnal Mengajar</DialogTitle>
            <DialogDescription>Rincian jurnal mengajar termasuk KD/Indikator, materi, dan kehadiran siswa</DialogDescription>
          </DialogHeader>
          {detailDialog.journal && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Tanggal & Waktu</Label>
                  <div className="font-semibold">{formatWIB(detailDialog.journal.started_at, '', { dateStyle: 'long', timeStyle: 'short' })}</div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Jam Ke</Label>
                  <div className="font-semibold">{detailDialog.journal.jam_ke || '-'}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Kelas</Label>
                  <div className="font-semibold">{detailDialog.journal.class_name}</div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Mata Pelajaran</Label>
                  <div className="font-semibold">
                    <Badge variant="outline" className="mr-1">{detailDialog.journal.subject_code}</Badge>
                    {detailDialog.journal.subject_name}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Guru Pengajar</Label>
                  <div className="font-semibold">{detailDialog.journal.teacher_name}</div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Ruangan</Label>
                  <div className="font-semibold font-mono">{detailDialog.journal.room_name || '-'}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">JTM</Label>
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300 font-semibold">
                    {detailDialog.journal.jtm_count || 1} JTM
                  </Badge>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Diisi Oleh</Label>
                  <div>
                    {detailDialog.journal.fill_mode === 'substitute' ? (
                      <SubstituteFilledBadge journal={detailDialog.journal} />
                    ) : detailDialog.journal.fill_mode === 'piket' ? (
                      <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                        ✋ Piket{detailDialog.journal.filled_by_name ? `: ${detailDialog.journal.filled_by_name}` : ''}
                      </Badge>
                    ) : detailDialog.journal.fill_mode === 'admin' ? (
                      <Badge className="bg-purple-100 text-purple-700 border-purple-200">Admin</Badge>
                    ) : (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Pengajar</Badge>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Asal Input</Label>
                  <div><BadgeAsalInput journal={detailDialog.journal} /></div>
                </div>
              </div>

              {detailDialog.journal.kd_indikator && (
                <div>
                  <Label className="text-xs text-slate-500">KD/Indikator</Label>
                  <Card className="mt-1 border-[#006837]/20 bg-[#006837]/5">
                    <CardContent className="p-3">
                      <div className="text-sm font-medium text-[#006837]">{detailDialog.journal.kd_indikator}</div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {detailDialog.journal.materi_nama && (
                <div>
                  <Label className="text-xs text-slate-500">Materi/Pokok Bahasan (Terdaftar)</Label>
                  <Card className="mt-1 border-blue-200 bg-blue-50">
                    <CardContent className="p-3">
                      <div className="text-sm font-medium text-blue-700">{detailDialog.journal.materi_nama}</div>
                    </CardContent>
                  </Card>
                </div>
              )}

              <div>
                <Label className="text-xs text-slate-500">Materi yang Diajarkan</Label>
                <Card className="mt-1">
                  <CardContent className="p-3">
                    <div className="text-sm whitespace-pre-wrap">{detailDialog.journal.materi || '-'}</div>
                  </CardContent>
                </Card>
              </div>

              <div>
                <Label className="text-xs text-slate-500">Jenis Izin</Label>
                <div className="mt-1">
                  {detailDialog.journal.jenis_izin ? (
                    <Badge className="bg-blue-50 text-blue-700 border-blue-300">{detailDialog.journal.jenis_izin}</Badge>
                  ) : (
                    <span className="text-slate-400 text-sm">Tidak ada izin</span>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-xs text-slate-500">Catatan</Label>
                <Card className="mt-1">
                  <CardContent className="p-3">
                    <div className="text-sm whitespace-pre-wrap">{detailDialog.journal.catatan || '-'}</div>
                  </CardContent>
                </Card>
              </div>

              {/* Ringkasan Kehadiran */}
              <div>
                <Label className="text-sm font-semibold mb-2 block">Ringkasan Kehadiran Siswa</Label>
                <div className="grid grid-cols-4 gap-3">
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded">
                    <div className="text-xs text-emerald-600 mb-1">Hadir</div>
                    <div className="text-2xl font-bold text-emerald-700">{detailDialog.journal.siswa_hadir || 0}</div>
                  </div>
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                    <div className="text-xs text-amber-600 mb-1">Sakit</div>
                    <div className="text-2xl font-bold text-amber-700">{detailDialog.journal.siswa_sakit || 0}</div>
                  </div>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                    <div className="text-xs text-blue-600 mb-1">Izin</div>
                    <div className="text-2xl font-bold text-blue-700">{detailDialog.journal.siswa_izin || 0}</div>
                  </div>
                  <div className="p-3 bg-rose-50 border border-rose-200 rounded">
                    <div className="text-xs text-rose-600 mb-1">Alpa</div>
                    <div className="text-2xl font-bold text-rose-700">{detailDialog.journal.siswa_tidak_hadir || 0}</div>
                  </div>
                </div>
              </div>

              {/* Detail Kehadiran Siswa */}
              {loadingAttendance ? (
                <div className="flex justify-center items-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-green-600" />
                </div>
              ) : attendanceDetail && (
                <div>
                  <Label className="text-sm font-semibold mb-2 block">Detail Kehadiran Siswa</Label>
                  <KehadiranPerkiraanNotice show={attendanceDetail.kehadiran_perkiraan} />
                  <div className="grid grid-cols-1 gap-3">
                    {/* Siswa Hadir */}
                    {attendanceDetail.attendance.hadir.length > 0 && (
                      <Card className="border-emerald-200">
                        <CardHeader className="bg-emerald-50 py-2 px-3">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                            <span className="text-sm font-semibold text-emerald-700">
                              Hadir ({attendanceDetail.attendance.hadir.length})
                            </span>
                          </div>
                        </CardHeader>
                        <CardContent className="p-3">
                          <div className="grid grid-cols-2 gap-2">
                            {attendanceDetail.attendance.hadir.map((student, idx) => (
                              <div key={idx} className="text-sm bg-emerald-50 p-2 rounded border border-emerald-200">
                                {student.student_name}
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Siswa Sakit */}
                    {attendanceDetail.attendance.sakit.length > 0 && (
                      <Card className="border-amber-200">
                        <CardHeader className="bg-amber-50 py-2 px-3">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                            <span className="text-sm font-semibold text-amber-700">
                              Sakit ({attendanceDetail.attendance.sakit.length})
                            </span>
                          </div>
                        </CardHeader>
                        <CardContent className="p-3">
                          <div className="grid grid-cols-2 gap-2">
                            {attendanceDetail.attendance.sakit.map((student, idx) => (
                              <div key={idx} className="text-sm bg-amber-50 p-2 rounded border border-amber-200">
                                {student.student_name}
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Siswa Izin */}
                    {attendanceDetail.attendance.izin.length > 0 && (
                      <Card className="border-blue-200">
                        <CardHeader className="bg-blue-50 py-2 px-3">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                            <span className="text-sm font-semibold text-blue-700">
                              Izin ({attendanceDetail.attendance.izin.length})
                            </span>
                          </div>
                        </CardHeader>
                        <CardContent className="p-3">
                          <div className="grid grid-cols-2 gap-2">
                            {attendanceDetail.attendance.izin.map((student, idx) => (
                              <div key={idx} className="text-sm bg-blue-50 p-2 rounded border border-blue-200">
                                {student.student_name}
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Siswa Alpha */}
                    {attendanceDetail.attendance.alpha.length > 0 && (
                      <Card className="border-rose-200">
                        <CardHeader className="bg-rose-50 py-2 px-3">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-rose-500"></div>
                            <span className="text-sm font-semibold text-rose-700">
                              Alpha ({attendanceDetail.attendance.alpha.length})
                            </span>
                          </div>
                        </CardHeader>
                        <CardContent className="p-3">
                          <div className="grid grid-cols-2 gap-2">
                            {attendanceDetail.attendance.alpha.map((student, idx) => (
                              <div key={idx} className="text-sm bg-rose-50 p-2 rounded border border-rose-200">
                                {student.student_name}
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setDetailDialog({ open: false, journal: null })}>
                  Tutup
                </Button>
                {canEdit && (
                  <Button onClick={() => {
                    setDetailDialog({ open: false, journal: null });
                    openEditDialog(detailDialog.journal);
                  }} className="bg-[#006837] hover:bg-[#0B7A3B]">
                    <Edit className="h-4 w-4 mr-1" /> Edit Jurnal
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => !editSaving && setEditDialog({ open, journal: null })}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Jurnal Mengajar</DialogTitle>
            <DialogDescription>Ubah materi, catatan, dan data kehadiran siswa pada jurnal ini</DialogDescription>
          </DialogHeader>
          {editDialog.journal && (
            <div className="space-y-4">
              {/* Journal Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Tanggal & Waktu</Label>
                  <div className="font-semibold">{formatWIB(editDialog.journal.started_at, '', { dateStyle: 'long', timeStyle: 'short' })}</div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Jam Ke</Label>
                  <div className="font-semibold">{editDialog.journal.jam_ke || '-'}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Kelas</Label>
                  <div className="font-semibold">{editDialog.journal.class_name}</div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Mata Pelajaran</Label>
                  <div className="font-semibold">
                    <Badge variant="outline" className="mr-1">{editDialog.journal.subject_code}</Badge>
                    {editDialog.journal.subject_name}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Guru Pengajar</Label>
                  <div className="font-semibold">{editDialog.journal.teacher_name}</div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Ruangan</Label>
                  <div className="font-semibold font-mono">{editDialog.journal.room_name || '-'}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">JTM</Label>
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300 font-semibold">
                    {editDialog.journal.jtm_count || 1} JTM
                  </Badge>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Mode Pengisian</Label>
                  <div>
                    {editDialog.journal.fill_mode === 'substitute' ? (
                      <SubstituteFilledBadge journal={editDialog.journal} />
                    ) : editDialog.journal.fill_mode === 'piket' ? (
                      <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                        Piket{editDialog.journal.filled_by_name ? `: ${editDialog.journal.filled_by_name}` : ''}
                      </Badge>
                    ) : editDialog.journal.fill_mode === 'admin' ? (
                      <Badge className="bg-purple-100 text-purple-700 border-purple-200">Admin</Badge>
                    ) : (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Pengajar</Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Materi */}
              <div>
                <Label>Materi Pembelajaran *</Label>
                <Textarea
                  value={editForm.materi}
                  onChange={(e) => setEditForm({ ...editForm, materi: e.target.value })}
                  placeholder="Masukkan materi pembelajaran..."
                  rows={4}
                  className="mt-1"
                />
              </div>

              {/* Catatan */}
              <div>
                <Label>Catatan</Label>
                <Textarea
                  value={editForm.catatan}
                  onChange={(e) => setEditForm({ ...editForm, catatan: e.target.value })}
                  placeholder="Catatan tambahan (opsional)..."
                  rows={2}
                  className="mt-1"
                />
              </div>

              {/* Jenis Izin */}
              <div>
                <Label>Jenis Izin Guru</Label>
                <Select
                  value={editForm.jenis_izin || 'none'}
                  onValueChange={(v) => setEditForm({ ...editForm, jenis_izin: v === 'none' ? null : v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Pilih jenis izin..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Tidak ada izin</SelectItem>
                    <SelectItem value="sakit">Izin Sakit</SelectItem>
                    <SelectItem value="cuti">Izin Cuti</SelectItem>
                    <SelectItem value="dinas_luar">Dinas Luar</SelectItem>
                    <SelectItem value="lainnya">Izin Lainnya</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Student Attendance */}
              <div>
                <Label className="text-sm font-semibold mb-2 block">Kehadiran Siswa</Label>
                <KehadiranPerkiraanNotice show={editDialog.journal.kehadiran_perkiraan} bisaEdit />

                {/* Ringkasan Kehadiran */}
                {editForm.attendances.length > 0 && (
                  <div className="grid grid-cols-4 gap-3 mb-3">
                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded">
                      <div className="text-xs text-emerald-600 mb-1">Hadir</div>
                      <div className="text-2xl font-bold text-emerald-700">
                        {editForm.attendances.filter(a => a.status === 'hadir').length}
                      </div>
                    </div>
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                      <div className="text-xs text-amber-600 mb-1">Sakit</div>
                      <div className="text-2xl font-bold text-amber-700">
                        {editForm.attendances.filter(a => a.status === 'sakit').length}
                      </div>
                    </div>
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                      <div className="text-xs text-blue-600 mb-1">Izin</div>
                      <div className="text-2xl font-bold text-blue-700">
                        {editForm.attendances.filter(a => a.status === 'izin').length}
                      </div>
                    </div>
                    <div className="p-3 bg-rose-50 border border-rose-200 rounded">
                      <div className="text-xs text-rose-600 mb-1">Alpa</div>
                      <div className="text-2xl font-bold text-rose-700">
                        {editForm.attendances.filter(a => a.status === 'alpa').length}
                      </div>
                    </div>
                  </div>
                )}

                <Card>
                  <CardContent className="p-0">
                    <div className="max-h-[300px] overflow-y-auto">
                      <Table>
                        <TableHeader className="sticky top-0 bg-white z-10">
                          <TableRow>
                            <TableHead className="w-12">#</TableHead>
                            <TableHead>Nama Siswa</TableHead>
                            <TableHead className="text-center">Status Kehadiran</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {editForm.attendances.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={3} className="text-center py-4 text-slate-500">
                                Tidak ada data kehadiran
                              </TableCell>
                            </TableRow>
                          ) : (
                            editForm.attendances.map((att, idx) => (
                              <TableRow key={att.student_id}>
                                <TableCell className="text-center text-sm text-slate-500">{idx + 1}</TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <User className="h-4 w-4 text-slate-400" />
                                    <span className="font-medium">{att.student_name}</span>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Select
                                    value={att.status}
                                    onValueChange={(newStatus) => updateAttendanceStatus(att.student_id, newStatus)}
                                  >
                                    <SelectTrigger className="w-32 mx-auto">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="hadir">
                                        <span className="flex items-center gap-2">
                                          <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                                          Hadir
                                        </span>
                                      </SelectItem>
                                      <SelectItem value="sakit">
                                        <span className="flex items-center gap-2">
                                          <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                                          Sakit
                                        </span>
                                      </SelectItem>
                                      <SelectItem value="izin">
                                        <span className="flex items-center gap-2">
                                          <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                                          Izin
                                        </span>
                                      </SelectItem>
                                      <SelectItem value="alpa">
                                        <span className="flex items-center gap-2">
                                          <span className="w-2 h-2 rounded-full bg-rose-500"></span>
                                          Alpa
                                        </span>
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setEditDialog({ open: false, journal: null })}
                  disabled={editSaving}
                >
                  Batal
                </Button>
                <Button
                  onClick={handleEditSave}
                  className="bg-[#006837] hover:bg-[#0B7A3B]"
                  disabled={editSaving}
                >
                  {editSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      Menyimpan...
                    </>
                  ) : (
                    'Simpan Perubahan'
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ label, value, color, testid }) {
  const colors = {
    slate: 'bg-white border-slate-200 text-slate-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    rose: 'bg-rose-50 border-rose-200 text-rose-700',
  };
  return (
    <div className={`rounded-xl border p-3 ${colors[color]}`}>
      <div className="text-xs font-semibold uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-2xl font-extrabold tabular-nums" data-testid={testid}>{value}</div>
    </div>
  );
}
